import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from './App';
import { useAuth } from './App';
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  addDoc,
  Timestamp
} from 'firebase/firestore';
import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth';

const AdminPanel = () => {
  const navigate = useNavigate();
  const { isAdmin, logout } = useAuth();
  const [shops, setShops] = useState([]);
  const [users, setUsers] = useState([]);
  const [betaTokens, setBetaTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('shops');
  const [showCreateShop, setShowCreateShop] = useState(false);
  const [showCreateUser, setShowCreateUser] = useState(false);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }

    const shopsQuery = query(collection(db, 'shops'), orderBy('createdAt', 'desc'));
    const usersQuery = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
    const betaQuery = query(collection(db, 'beta_tokens'), orderBy('createdAt', 'desc'));

    const unsubShops = onSnapshot(shopsQuery, (snap) => {
      setShops(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, (error) => {
      console.error("Error fetching shops:", error);
    });

    const unsubUsers = onSnapshot(usersQuery, (snap) => {
      setUsers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, (error) => {
      console.error("Error fetching users:", error);
    });

    const unsubBeta = onSnapshot(betaQuery, (snap) => {
      setBetaTokens(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (error) => {
      console.error("Error fetching beta tokens:", error);
      setLoading(false); // Ensure loading stops even on error
    });

    return () => {
      unsubShops();
      unsubUsers();
      unsubBeta();
    };
  }, [isAdmin, navigate]);

  const updateShopStatus = async (shopId, status) => {
    await updateDoc(doc(db, 'shops', shopId), {
      status: status,
      updatedAt: Timestamp.now()
    });
  };

  const deleteShop = async (shopId) => {
    if (!window.confirm('Are you sure you want to delete this shop?')) return;
    await deleteDoc(doc(db, 'shops', shopId));
  };

  const generateBetaToken = async () => {
    try {
      await addDoc(collection(db, 'beta_tokens'), {
        createdAt: Timestamp.now(),
        expiresAt: Timestamp.fromDate(new Date(Date.now() + 60 * 24 * 60 * 60 * 1000)), // 60 days
        used: false,
        shopId: null,
        durationDays: 60,
        type: 'beta'
      });
    } catch (err) {
      alert('Failed to generate token: ' + err.message);
    }
  };

  const generateFounderToken = async () => {
    try {
      await addDoc(collection(db, 'beta_tokens'), {
        createdAt: Timestamp.now(),
        expiresAt: Timestamp.fromDate(new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)), // 1 year
        used: false,
        shopId: null,
        durationDays: 36500, // 100 years
        type: 'founder'
      });
    } catch (err) {
      alert('Failed to generate token: ' + err.message);
    }
  };

  const getStatusBadge = (status) => {
    if (status === 'active') return 'bg-emerald-100 text-emerald-800';
    if (status === 'trial') return 'bg-blue-100 text-blue-800';
    if (status === 'suspended') return 'bg-red-100 text-red-800';
    return 'bg-slate-100 text-slate-800';
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-white text-center">
          <h1 className="text-2xl font-bold mb-2">Access Denied</h1>
          <p className="text-slate-400">You do not have permission to view this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="bg-slate-900 text-white sticky top-0 z-50 shadow-xl">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">
                <span className="text-red-500">*</span> Admin Panel
              </h1>
              <p className="text-slate-400 text-sm">Authorization Hook Management</p>
            </div>
            <div className="flex items-center space-x-4">
              <button onClick={() => navigate('/dashboard')} className="px-3 py-1 bg-slate-700 rounded text-sm">Dashboard</button>
              <button onClick={() => { logout(); navigate('/login'); }} className="px-3 py-1 bg-slate-700 rounded text-sm">Logout</button>
            </div>
          </div>
        </div>
      </header>

      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex space-x-4">
            <button
              onClick={() => setActiveTab('shops')}
              className={'px-4 py-3 font-medium text-sm border-b-2 transition-all ' + (activeTab === 'shops' ? 'border-amber-500 text-amber-600' : 'border-transparent text-slate-600 hover:text-slate-900')}
            >
              Shops ({shops.length})
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={'px-4 py-3 font-medium text-sm border-b-2 transition-all ' + (activeTab === 'users' ? 'border-amber-500 text-amber-600' : 'border-transparent text-slate-600 hover:text-slate-900')}
            >
              Users ({users.length})
            </button>
            <button
              onClick={() => setActiveTab('beta')}
              className={'px-4 py-3 font-medium text-sm border-b-2 transition-all ' + (activeTab === 'beta' ? 'border-amber-500 text-amber-600' : 'border-transparent text-slate-600 hover:text-slate-900')}
            >
              Beta Invites ({betaTokens.filter(t => !t.used).length})
            </button>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <div>
            {activeTab === 'shops' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-bold text-slate-800">Registered Shops</h2>
                  <button
                    onClick={() => setShowCreateShop(true)}
                    className="px-4 py-2 bg-amber-500 text-white rounded-lg font-medium hover:bg-amber-600"
                  >
                    + New Shop
                  </button>
                </div>

                <div className="bg-white rounded-xl shadow overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Shop</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Owner</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Created</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {shops.map(shop => (
                        <tr key={shop.id} className="hover:bg-slate-50">
                          <td className="px-6 py-4">
                            <div>
                              <p className="font-medium text-slate-900">{shop.shopName}</p>
                              <p className="text-sm text-slate-500 font-mono">{shop.id}</p>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div>
                              <p className="text-slate-900">{shop.ownerName}</p>
                              <p className="text-sm text-slate-500">{shop.ownerEmail}</p>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className={'px-2 py-1 rounded-full text-xs font-medium ' + getStatusBadge(shop.status)}>
                              {shop.status ? shop.status.toUpperCase() : 'UNKNOWN'}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-slate-500">
                            {shop.createdAt && shop.createdAt.toDate ? shop.createdAt.toDate().toLocaleDateString() : 'N/A'}
                          </td>
                          <td className="px-6 py-4 text-right space-x-2">
                            {shop.status !== 'active' && (
                              <button onClick={() => updateShopStatus(shop.id, 'active')} className="text-emerald-600 hover:text-emerald-800 text-sm font-medium">Activate</button>
                            )}
                            {shop.status !== 'suspended' && (
                              <button onClick={() => updateShopStatus(shop.id, 'suspended')} className="text-red-600 hover:text-red-800 text-sm font-medium">Suspend</button>
                            )}
                            <button onClick={() => deleteShop(shop.id)} className="text-slate-400 hover:text-slate-600 text-sm font-medium">Delete</button>
                          </td>
                        </tr>
                      ))}
                      {shops.length === 0 && (
                        <tr>
                          <td colSpan={5} className="px-6 py-12 text-center text-slate-500">No shops registered yet</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'users' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-bold text-slate-800">User Accounts</h2>
                  <button
                    onClick={() => setShowCreateUser(true)}
                    className="px-4 py-2 bg-amber-500 text-white rounded-lg font-medium hover:bg-amber-600"
                  >
                    + New User
                  </button>
                </div>

                <div className="bg-white rounded-xl shadow overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">User</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Role</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Shop</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Created</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {users.map(u => {
                        const userShop = shops.find(s => s.id === u.shopId);
                        return (
                          <tr key={u.id} className="hover:bg-slate-50">
                            <td className="px-6 py-4">
                              <div>
                                <p className="font-medium text-slate-900">{u.name || u.email}</p>
                                <p className="text-sm text-slate-500">{u.email}</p>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <span className={'px-2 py-1 rounded-full text-xs font-medium ' + (u.role === 'admin' ? 'bg-red-100 text-red-800' : u.role === 'owner' ? 'bg-purple-100 text-purple-800' : 'bg-slate-100 text-slate-800')}>
                                {u.role ? u.role.toUpperCase() : 'USER'}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-slate-600">
                              {userShop ? userShop.shopName : (u.shopId || 'N/A')}
                            </td>
                            <td className="px-6 py-4 text-sm text-slate-500">
                              {u.createdAt && u.createdAt.toDate ? u.createdAt.toDate().toLocaleDateString() : 'N/A'}
                            </td>
                            <td className="px-6 py-4 text-right">
                              {u.role !== 'admin' && (
                                <button onClick={() => deleteDoc(doc(db, 'users', u.id))} className="text-slate-400 hover:text-slate-600 text-sm font-medium">Delete</button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      {users.length === 0 && (
                        <tr>
                          <td colSpan={5} className="px-6 py-12 text-center text-slate-500">No users found</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'beta' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-bold text-slate-800">Beta Invites</h2>
                  <div className="space-x-3">
                    <button
                      onClick={generateBetaToken}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700"
                    >
                      + Beta (60 Days)
                    </button>
                    <button
                      onClick={generateFounderToken}
                      className="px-4 py-2 bg-amber-500 text-white rounded-lg font-medium hover:bg-amber-600"
                    >
                      + Founder (Lifetime)
                    </button>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Type</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Token / Link</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Created</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Used By</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {betaTokens.map(token => (
                        <tr key={token.id} className="hover:bg-slate-50">
                          <td className="px-6 py-4">
                            <span className={'px-2 py-1 rounded-full text-xs font-medium ' + (token.used ? 'bg-slate-100 text-slate-500' : 'bg-emerald-100 text-emerald-800')}>
                              {token.used ? 'USED' : 'ACTIVE'}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className={'px-2 py-1 rounded-full text-xs font-medium ' + (token.type === 'founder' ? 'bg-amber-100 text-amber-800' : 'bg-purple-100 text-purple-800')}>
                              {token.type === 'founder' ? 'FOUNDER' : 'BETA'}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-2">
                              <code className="text-xs bg-slate-100 p-1 rounded border">{token.id}</code>
                              {!token.used && (
                                <button 
                                  onClick={() => navigator.clipboard.writeText(window.location.origin + '/signup?token=' + token.id)}
                                  className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                                >
                                  Copy Link
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-slate-500">
                            {token.createdAt && token.createdAt.toDate ? token.createdAt.toDate().toLocaleDateString() : 'N/A'}
                          </td>
                          <td className="px-6 py-4 text-sm text-slate-500">
                            {token.shopId || '-'}
                          </td>
                          <td className="px-6 py-4 text-right">
                            <button onClick={() => deleteDoc(doc(db, 'beta_tokens', token.id))} className="text-slate-400 hover:text-slate-600 text-sm font-medium">Delete</button>
                          </td>
                        </tr>
                      ))}
                      {betaTokens.length === 0 && (
                        <tr>
                          <td colSpan={5} className="px-6 py-12 text-center text-slate-500">No beta tokens generated</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {showCreateShop && (
        <CreateShopModal onClose={() => setShowCreateShop(false)} />
      )}

      {showCreateUser && (
        <CreateUserModal shops={shops} onClose={() => setShowCreateUser(false)} />
      )}
    </div>
  );
};

const CreateShopModal = ({ onClose }) => {
  const [formData, setFormData] = useState({
    shopName: '',
    ownerName: '',
    ownerEmail: '',
    ownerPhone: '',
    street: '',
    city: '',
    state: '',
    zipCode: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const shopId = formData.shopName.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').substring(0, 20);
      
      await setDoc(doc(db, 'shops', shopId), {
        shopName: formData.shopName,
        ownerName: formData.ownerName,
        ownerEmail: formData.ownerEmail,
        ownerPhone: formData.ownerPhone,
        address: {
          street: formData.street,
          city: formData.city,
          state: formData.state,
          zipCode: formData.zipCode,
          full: formData.street + ', ' + formData.city + ', ' + formData.state + ' ' + formData.zipCode
        },
        status: 'trial',
        trialEndsAt: Timestamp.fromDate(new Date(Date.now() + 14 * 24 * 60 * 60 * 1000)),
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });

      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-screen overflow-y-auto">
        <div className="p-6 border-b">
          <h2 className="text-xl font-bold text-slate-800">Create New Shop</h2>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Shop Name *</label>
            <input type="text" name="shopName" value={formData.shopName} onChange={handleChange} required className="w-full px-4 py-2 border border-slate-300 rounded-lg" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Owner Name *</label>
              <input type="text" name="ownerName" value={formData.ownerName} onChange={handleChange} required className="w-full px-4 py-2 border border-slate-300 rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Owner Email *</label>
              <input type="email" name="ownerEmail" value={formData.ownerEmail} onChange={handleChange} required className="w-full px-4 py-2 border border-slate-300 rounded-lg" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Owner Phone</label>
            <input type="tel" name="ownerPhone" value={formData.ownerPhone} onChange={handleChange} className="w-full px-4 py-2 border border-slate-300 rounded-lg" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Street Address</label>
            <input type="text" name="street" value={formData.street} onChange={handleChange} className="w-full px-4 py-2 border border-slate-300 rounded-lg" />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">City</label>
              <input type="text" name="city" value={formData.city} onChange={handleChange} className="w-full px-4 py-2 border border-slate-300 rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">State</label>
              <input type="text" name="state" value={formData.state} onChange={handleChange} className="w-full px-4 py-2 border border-slate-300 rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">ZIP</label>
              <input type="text" name="zipCode" value={formData.zipCode} onChange={handleChange} className="w-full px-4 py-2 border border-slate-300 rounded-lg" />
            </div>
          </div>
          {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>}
          <div className="flex space-x-3 pt-4">
            <button type="button" onClick={onClose} className="flex-1 py-2 bg-slate-200 text-slate-700 rounded-lg font-medium">Cancel</button>
            <button type="submit" disabled={loading} className="flex-1 py-2 bg-amber-500 text-white rounded-lg font-medium hover:bg-amber-600 disabled:opacity-50">
              {loading ? 'Creating...' : 'Create Shop'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CreateUserModal = ({ shops, onClose }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'advisor',
    shopId: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const auth = getAuth();
      const userCred = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
      
      await setDoc(doc(db, 'users', userCred.user.uid), {
        email: formData.email,
        name: formData.name,
        role: formData.role,
        shopId: formData.shopId || null,
        createdAt: Timestamp.now()
      });

      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full">
        <div className="p-6 border-b">
          <h2 className="text-xl font-bold text-slate-800">Create New User</h2>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
            <input type="email" name="email" value={formData.email} onChange={handleChange} required className="w-full px-4 py-2 border border-slate-300 rounded-lg" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Password *</label>
            <input type="password" name="password" value={formData.password} onChange={handleChange} required minLength={6} className="w-full px-4 py-2 border border-slate-300 rounded-lg" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
            <input type="text" name="name" value={formData.name} onChange={handleChange} className="w-full px-4 py-2 border border-slate-300 rounded-lg" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Role *</label>
            <select name="role" value={formData.role} onChange={handleChange} className="w-full px-4 py-2 border border-slate-300 rounded-lg bg-white">
              <option value="advisor">Advisor</option>
              <option value="owner">Owner</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Shop</label>
            <select name="shopId" value={formData.shopId} onChange={handleChange} className="w-full px-4 py-2 border border-slate-300 rounded-lg bg-white">
              <option value="">No Shop (Admin only)</option>
              {shops.map(s => (
                <option key={s.id} value={s.id}>{s.shopName}</option>
              ))}
            </select>
          </div>
          {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>}
          <div className="flex space-x-3 pt-4">
            <button type="button" onClick={onClose} className="flex-1 py-2 bg-slate-200 text-slate-700 rounded-lg font-medium">Cancel</button>
            <button type="submit" disabled={loading} className="flex-1 py-2 bg-amber-500 text-white rounded-lg font-medium hover:bg-amber-600 disabled:opacity-50">
              {loading ? 'Creating...' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminPanel;

